/**
 * API scopes — what a machine credential is allowed to ask for.
 *
 * A credential (an API key from Settings, or an access token an app was given
 * through OAuth) carries a set of scopes, and every request is decided by two
 * independent limits:
 *
 *   1. THE SCOPES it was granted. Each one unlocks a named part of the API and
 *      nothing else, and the surface table (worker/lib/api-surface.ts) says
 *      which scope each endpoint needs. Nothing is implied: a credential that
 *      was not given `messaging.send` cannot text a customer however wide the
 *      rest of it is.
 *   2. THE PERMISSIONS OF THE USER it belongs to. The scope set is intersected
 *      with what that person's role can actually do at the credential's
 *      location, so a credential is never a way to gain reach its holder does
 *      not have — and a demotion or a deactivation narrows or ends every
 *      credential they hold, on the next request, with nothing to remember.
 *
 * That pairing is what lets the API be wide without being dangerous. Every
 * capability the app has is reachable, including the ones that send messages,
 * move money and delete records — but each is off unless it was explicitly
 * asked for, named on the consent screen, and backed by the user's own role.
 *
 * The presets below exist because most people are not going to read a list of
 * twenty-five scopes: "Read only" and "Read and write" cover the ordinary
 * integrations, and "Custom" is there when something needs more.
 */

import type { Permission } from './index.ts';

export const API_SCOPES = [
  // --- Reading the company's own configuration -----------------------------
  'company.read',
  'locations.read',
  'templates.read',
  'reports.read',
  'activity.read',
  // --- Customers -----------------------------------------------------------
  'customers.read',
  'customers.write',
  'customers.delete',
  // --- Quotes --------------------------------------------------------------
  'quotes.read',
  'quotes.write',
  'quotes.publish',
  'quotes.delete',
  // --- Leads (the public quote-request form) -------------------------------
  'leads.read',
  'leads.write',
  // --- Schedule ------------------------------------------------------------
  'schedule.read',
  'schedule.write',
  // --- Money ---------------------------------------------------------------
  'invoices.read',
  'payments.read',
  'payments.write',
  'payments.refund',
  // --- Things that leave the building --------------------------------------
  'messaging.send',
  'documents.generate',
  'files.read',
  'files.write',
  // --- Configuration -------------------------------------------------------
  'templates.write',
  'company.write',
] as const;

export type ApiScope = (typeof API_SCOPES)[number];

export function isApiScope(value: unknown): value is ApiScope {
  return typeof value === 'string' && (API_SCOPES as readonly string[]).includes(value);
}

/**
 * The permissions a scope may unlock — a ceiling, never a grant. The user's
 * own role is the other half of the intersection, so `payments.refund` on a
 * key belonging to an estimator unlocks nothing at all.
 */
export const SCOPE_PERMISSIONS: Readonly<Record<ApiScope, readonly Permission[]>> = {
  'company.read': ['company:read'],
  'company.write': ['company:write'],
  'locations.read': ['company:read'],
  'templates.read': ['templates:read'],
  'templates.write': ['templates:write'],
  'reports.read': ['reports:read'],
  'activity.read': ['logs:read'],
  'customers.read': ['customers:read'],
  'customers.write': ['customers:write'],
  'customers.delete': ['customers:delete'],
  'quotes.read': ['quotes:read'],
  // `quotes:status_override` rides with the write scope rather than getting one
  // of its own: it is the same act — moving a quote along — and the role
  // intersection is what actually decides it, since only a manager or an owner
  // has that permission at all.
  'quotes.write': ['quotes:write', 'quotes:status_override', 'quotes:price_override'],
  'quotes.publish': ['quotes:write'],
  'quotes.delete': ['quotes:delete'],
  'leads.read': ['quotes:read'],
  // Converting a lead CREATES a customer — that is what conversion means —
  // so the scope that advertises it has to carry the write the route asserts.
  // Without it a credential could dismiss leads and never convert one, which
  // is the half of the scope nobody grants it for.
  'leads.write': ['quotes:write', 'customers:write'],
  'schedule.read': ['schedule:read'],
  'schedule.write': ['schedule:write'],
  'invoices.read': ['payments:read'],
  'payments.read': ['payments:read'],
  'payments.write': ['payments:write'],
  'payments.refund': ['payments:refund'],
  // Sending a quote or a payment request is a quote/payment write behind the
  // scenes, which is why this scope has to exist separately: without it,
  // "update a quote" and "email it to the customer" would be the same grant.
  'messaging.send': ['quotes:write', 'customers:write', 'payments:write'],
  'documents.generate': ['pdf:generate'],
  // Everything downloadable, because "files" is one idea to the person
  // granting it and three permissions to the routes behind it: a quote's
  // attachments are `quotes:read`, a visit's photos `schedule:read`, an
  // invoice PDF `payments:read`. A ceiling narrower than the endpoints it
  // advertises is a scope that refuses the very things it was granted for —
  // and it is still only a ceiling, so a credential whose user cannot see
  // visits still cannot download their photos.
  'files.read': ['quotes:read', 'schedule:read', 'payments:read'],
  'files.write': ['files:write'],
};

/**
 * Scopes whose consequences leave the building — a customer receives
 * something, money moves, or a record stops existing. Flagged so the consent
 * screen and Settings can say so plainly rather than listing twenty-five
 * equal-looking lines.
 */
export const SENSITIVE_SCOPES: readonly ApiScope[] = [
  'customers.delete',
  'quotes.delete',
  // Not just "can upload": the same scope carries DELETE /files/:fileId, which
  // removes the R2 object as well as the row. A customer's before-and-after
  // photos do not come back, so this belongs beside the other deletions on the
  // consent screen rather than reading as a harmless write.
  'files.write',
  'payments.write',
  'payments.refund',
  'messaging.send',
  'company.write',
  'templates.write',
  'activity.read',
];

export function isSensitiveScope(scope: ApiScope): boolean {
  return SENSITIVE_SCOPES.includes(scope);
}

/**
 * What each scope lets an app do, in the words the consent screen uses. The
 * scope code is still shown beside each line, for whoever wants it; this is
 * for the person deciding whether to click Connect, who should not have to
 * know what `leads.write` means.
 */
export const SCOPE_DESCRIPTIONS: Readonly<Record<ApiScope, string>> = {
  'company.read': 'See your company details and settings',
  'locations.read': 'See your locations',
  'templates.read': 'See your room and task library',
  'reports.read': 'See your dashboard and reports',
  'activity.read': 'See the activity log, including who did what',
  'customers.read': 'See your customers and their properties',
  'customers.write': 'Add customers and properties, and update them',
  'customers.delete': 'Delete customers',
  'quotes.read': 'See your quotes',
  'quotes.write': 'Create and update quotes',
  'quotes.publish': 'Publish quotes so customers can open them',
  'quotes.delete': 'Delete quotes',
  'leads.read': 'See the quote requests from your website',
  'leads.write': 'Turn quote requests into customers and draft quotes, or dismiss them',
  'schedule.read': 'See your schedule',
  'schedule.write': 'Book, move and cancel visits',
  'invoices.read': 'See your invoices and what is owed',
  'payments.read': 'See payments',
  'payments.write': 'Record payments and send payment requests',
  'payments.refund': 'Refund payments',
  'messaging.send': 'Send quotes, texts and emails to your customers',
  'documents.generate': 'Create PDFs of quotes and invoices',
  'files.read': 'Download attachments and photos',
  'files.write': 'Upload and delete attachments and photos',
  'templates.write': 'Change your room and task library and its prices',
  'company.write': 'Change your company settings',
};

// ---------------------------------------------------------------------------
// Presets
// ---------------------------------------------------------------------------

export const API_KEY_PRESETS = ['read', 'read_write', 'custom'] as const;
export type ApiKeyPreset = (typeof API_KEY_PRESETS)[number];

/** Everything that only ever looks at things. */
export const READ_SCOPES: readonly ApiScope[] = [
  'company.read',
  'locations.read',
  'templates.read',
  'customers.read',
  'quotes.read',
  'leads.read',
  'schedule.read',
  'invoices.read',
  'payments.read',
  'reports.read',
  // The attachments, photos and PDFs the records above point at. Left out, a
  // read preset could read a quote and then be refused the file it names —
  // which reads as a bug in the integration rather than a scope it was never
  // given. Downloading is the only thing it opens; deleting is `files.write`.
  'files.read',
];

/**
 * The ordinary integration: read everything, and create or update the records
 * an integration actually creates. Pointedly excludes deleting, sending,
 * refunding and reconfiguring — those are `custom`, because somebody should
 * have to choose them.
 */
export const READ_WRITE_SCOPES: readonly ApiScope[] = [
  ...READ_SCOPES,
  'customers.write',
  'quotes.write',
  'quotes.publish',
  'leads.write',
  'schedule.write',
];

/**
 * The most an AI assistant (ChatGPT, Claude — anything connected through the
 * MCP server at /mcp) is ever granted, whatever it asks for.
 *
 * Reading, adding customers, drafting quotes and converting leads: exactly the
 * scopes the MCP tools use (services/mcp/tools.ts — a test keeps the two
 * equal). Nothing that sends a message, moves money, books a visit or deletes
 * a record. A model reads text strangers wrote — a lead's message, a
 * customer's notes — and text can carry instructions, so the worst an
 * injected instruction can do with this set is leave a draft somebody still
 * has to send.
 */
export const AI_ASSISTANT_SCOPES: readonly ApiScope[] = [
  'templates.read',
  'reports.read',
  'customers.read',
  'customers.write',
  'quotes.read',
  'quotes.write',
  'leads.read',
  'leads.write',
  'schedule.read',
  'invoices.read',
];

export function presetScopes(preset: ApiKeyPreset): readonly ApiScope[] {
  if (preset === 'read') return READ_SCOPES;
  if (preset === 'read_write') return READ_WRITE_SCOPES;
  return [];
}

/** Which preset a scope set IS, for display. `custom` when it matches none. */
export function presetOf(scopes: readonly ApiScope[]): ApiKeyPreset {
  const same = (preset: readonly ApiScope[]) =>
    preset.length === scopes.length && preset.every((scope) => scopes.includes(scope));
  if (same(READ_SCOPES)) return 'read';
  if (same(READ_WRITE_SCOPES)) return 'read_write';
  return 'custom';
}

// ---------------------------------------------------------------------------
// Storage and wire format
// ---------------------------------------------------------------------------

/** Scopes travel space-separated, as OAuth 2 requires, and store that way too. */
export function serializeScopes(scopes: readonly ApiScope[]): string {
  return [...new Set(scopes)].join(' ');
}

/**
 * Parses a scope string, dropping anything unrecognized.
 *
 * Dropping rather than failing is deliberate in BOTH directions this is used.
 * Reading a stored row, an unknown scope means a credential minted by a newer
 * version of this app, and the safe reading of "I do not know what this
 * permits" is "it permits nothing" — not "refuse the request entirely", which
 * would take a working integration down on a rollback. On an OAuth request the
 * client is asking for something this deployment does not offer, and the
 * granted scope list it gets back tells it exactly what it actually has (RFC
 * 6749 §3.3).
 */
export function parseScopes(value: string | null | undefined): ApiScope[] {
  if (!value) return [];
  return [...new Set(value.split(/[\s,]+/).filter(isApiScope))];
}

/**
 * Whether a set of permissions can use a scope at all: whether it holds any
 * of the permissions the scope unlocks. Asked of a person's role before an AI
 * assistant is granted the scope (so the consent screen promises only what
 * that person can do), and of a connection's effective permissions before the
 * MCP server offers a tool (so an assistant is never shown one that could
 * only answer "not allowed").
 */
export function scopeUsable(permissions: readonly string[], scope: ApiScope): boolean {
  return SCOPE_PERMISSIONS[scope].some((permission) => permissions.includes(permission));
}

/** The permission ceiling of a scope set. */
export function scopePermissions(scopes: readonly ApiScope[]): readonly Permission[] {
  const permissions = new Set<Permission>();
  for (const scope of scopes) {
    for (const permission of SCOPE_PERMISSIONS[scope] ?? []) permissions.add(permission);
  }
  return [...permissions];
}

// ---------------------------------------------------------------------------
// The credential itself
// ---------------------------------------------------------------------------

/**
 * The prefix every key carries.
 *
 * Two jobs: it tells a person who finds one in a config file what they are
 * looking at, and it is a stable, distinctive string that secret scanners can
 * be pointed at. The shard hint follows it (`bck_s01.…`), so the lookup finds
 * the right database without a directory round trip — the same arrangement the
 * session cookie uses, and for the same reason.
 */
export const API_KEY_TOKEN_PREFIX = 'bck_';

/** 32 random bytes, as base64url — the same strength as a session token. */
export const API_KEY_SECRET_BYTES = 32;

/**
 * How much of the key the list shows. Enough to tell two keys apart in a
 * config file at a glance, far too little to be worth guessing the rest from:
 * what is displayed is the prefix, the shard hint and six characters of the
 * secret, leaving 186 bits unshown.
 */
export const API_KEY_HINT_SECRET_CHARS = 6;

export const API_KEY_NAME_MAX = 60;

/**
 * A ceiling on keys per company, which exists to bound the damage of a script
 * that mints one per run rather than to ration anything: nobody legitimately
 * needs a fiftieth live key, and revoked ones do not count towards it.
 * Access tokens minted by OAuth are excluded — they are short-lived by
 * construction and belong to a connected app, not to this list.
 */
export const API_KEY_MAX_ACTIVE_PER_COMPANY = 25;

/** Longest expiry a key may be minted with, and the offered choices. */
export const API_KEY_EXPIRY_DAYS_MAX = 730;
export const API_KEY_EXPIRY_DAY_CHOICES = [30, 90, 180, 365] as const;

/**
 * Requests per credential per window.
 *
 * Every call behind a key is database work on a shard shared with other tenants, and
 * a key is precisely the thing that gets put in a loop with no sleep in it.
 * 600 in five minutes is two a second sustained — comfortably more than any
 * sync or booking site needs, and a brake on a retry storm rather than a quota
 * anybody meets in normal use.
 */
export const API_KEY_RATE_LIMIT = 600;
export const API_KEY_RATE_WINDOW_SECONDS = 5 * 60;

// ---------------------------------------------------------------------------
// OAuth (migration 0054)
// ---------------------------------------------------------------------------

/**
 * Prefixes, so a credential found anywhere says what it is: `bck_` a key or
 * access token, `bcr_` a refresh token, `bcc_` an authorization code. Each
 * carries a shard hint after the prefix (`bcr_s01.…`) because the token
 * endpoint has no session to resolve one from.
 */
export const OAUTH_REFRESH_TOKEN_PREFIX = 'bcr_';
export const OAUTH_CODE_PREFIX = 'bcc_';

/**
 * An access token lasts an hour. Short because it is a bearer credential
 * traveling to somebody else's servers, and because the refresh token makes
 * the length invisible to a working integration — the only thing an hour costs
 * is one extra round trip per hour.
 */
export const OAUTH_ACCESS_TOKEN_MINUTES = 60;

/**
 * A refresh token lasts ninety days from its last use, and every use rotates
 * it. An integration anybody is actually using therefore never expires, while
 * one nobody has called in a quarter stops on its own — which is the right
 * default for a credential the company has forgotten it granted.
 */
export const OAUTH_REFRESH_TOKEN_DAYS = 90;
/**
 * How long after a refresh token is rotated away presenting it again is still
 * taken for a client's retry — it sent the refresh, lost the response, and is
 * trying again — rather than for a second holder (api-oauth.ts). Only the
 * immediately previous token, only by the client it was issued to. Past this,
 * or for any older generation, the grant is ended.
 */
export const OAUTH_REFRESH_REUSE_GRACE_SECONDS = 60;

/** A code is redeemed within seconds; minutes is generous for a slow network. */
export const OAUTH_CODE_MINUTES = 5;

/** How long the signed consent form is good for, in minutes. */
export const OAUTH_CONSENT_TTL_MINUTES = 15;
